import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import axiosInstance from "@/api/axiosInstance";
import { type AdminSize } from "./useAdmin";

// Sizes CRUD hooks
export const useSizes = (enabled: boolean = true) => {
    return useQuery<AdminSize[]>({
        queryKey: ['admin', 'sizes'],
        queryFn: async () => {
            const { data } = await axiosInstance.get('/products/sizes/');
            return data.results || data;
        },
        enabled,
    });
};

export const useSize = (id: number, enabled: boolean = true) => {
    return useQuery<AdminSize>({
        queryKey: ['admin', 'sizes', id],
        queryFn: async () => {
            const { data } = await axiosInstance.get(`/products/sizes/${id}/`);
            return data;
        },
        enabled: enabled && !!id,
    });
};

export const useCreateSize = () => {
    const queryClient = useQueryClient();

    return useMutation<AdminSize, Error, Omit<AdminSize, 'id' | 'created_at' | 'updated_at'>>({
        mutationFn: async (sizeData) => {
            const { data } = await axiosInstance.post('/products/sizes/', sizeData);
            return data;
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['admin', 'sizes'] });
        },
    });
};

export const useUpdateSize = () => {
    const queryClient = useQueryClient();

    return useMutation<AdminSize, Error, { id: number; data: Partial<Omit<AdminSize, 'id' | 'created_at' | 'updated_at'>> }>({
        mutationFn: async ({ id, data }) => {
            const { data: response } = await axiosInstance.patch(`/products/sizes/${id}/`, data);
            return response;
        },
        onSuccess: (_, { id }) => {
            queryClient.invalidateQueries({ queryKey: ['admin', 'sizes'] });
            queryClient.invalidateQueries({ queryKey: ['admin', 'sizes', id] });
        },
    });
};

export const useDeleteSize = () => {
    const queryClient = useQueryClient();

    return useMutation<void, Error, number>({
        mutationFn: async (id: number) => {
            await axiosInstance.delete(`/products/sizes/${id}/`);
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['admin', 'sizes'] });
        },
    });
};
